import React, { FC, useMemo } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { PaymentPlace } from '@funtech-int/yango-shared';

import { PlusPaymentPlace } from '@/shared/constants/payments';

import { getEmptyOffer, getPlaceByMultiOfferTab } from '../../lib/offers';
import { useMultiOfferTab } from '../../hooks/useMultiOfferTab';
import { useMultiOffers } from '../../hooks/useMultiOffers';
import { PaywallProvider } from '../../hooks/usePaywallContext';
import { PaywallMainSection } from '../PaywallMainSection/PaywallMainSection';
import { InfoBlocks } from '../InfoBlocks/InfoBlocks';

import { MultiOfferMainSection } from './_MultiOfferMainSection';

interface MultiOfferContentProps {
    forcedPlace?: PlusPaymentPlace;
    paymentPlace?: PaymentPlace;
    isFallbackOffer: boolean;
}

const MultiOfferContent: FC<MultiOfferContentProps> = ({
    forcedPlace,
    paymentPlace,
    isFallbackOffer,
}) => {
    const { tabId } = useMultiOfferTab();
    const place = forcedPlace ?? getPlaceByMultiOfferTab(tabId);

    const currentOffer = paymentPlace?.selectOfferBy({ place });
    const contextPaymentPlace = isFallbackOffer ? undefined : paymentPlace;
    const hasOffers = paymentPlace?.offers && Object.keys(paymentPlace?.offers).length > 0;
    const contextOffer = !hasOffers ? getEmptyOffer() : currentOffer;

    const mainSection = useMemo(() => {
        if (!hasOffers) return <PaywallMainSection />;
        if (isFallbackOffer) return <MultiOfferMainSection isFallback />;

        return <MultiOfferMainSection />;
    }, [hasOffers, isFallbackOffer]);

    return (
        <PaywallProvider offer={contextOffer} paymentPlace={contextPaymentPlace}>
            {mainSection}
            <InfoBlocks />
        </PaywallProvider>
    );
};

const MultiOfferContainer: FC = () => {
    const { forcedPlace, paymentPlace, isFallbackOffer } = useMultiOffers();

    return (
        <MultiOfferContent
            forcedPlace={forcedPlace}
            paymentPlace={paymentPlace}
            isFallbackOffer={isFallbackOffer}
        />
    );
};

export const MultiOfferPaywall = () => (
    <ErrorBoundary
        fallback={
            <MultiOfferContent forcedPlace={undefined} paymentPlace={undefined} isFallbackOffer />
        }
    >
        <MultiOfferContainer />
    </ErrorBoundary>
);
